import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import accountsAPI from "../../../../services/accounts";

export const getAdminMemberships = (memberships) =>
  (memberships || []).filter(
    (membership) =>
      membership?.id &&
      membership?.is_admin &&
      (!membership?.school || membership?.school?.status === "ACTIVE")
  );

const MembershipAdminNotifications = ({ membership, showSchoolName, toast }) => {
  const queryClient = useQueryClient();
  const queryKey = ["admin-notification-preferences", membership.id];
  const [savingId, setSavingId] = useState(null);
  const { data: preferences = [], isLoading, isError } = useQuery(
    queryKey,
    () => accountsAPI.GetAdminNotificationPreferences("", membership.id)
  );

  const handleToggle = async (preference, enabled) => {
    setSavingId(preference.id);
    try {
      const updated = await accountsAPI.UpdateAdminNotificationPreferences(
        "",
        membership.id,
        { [preference.id]: enabled }
      );
      queryClient.setQueryData(queryKey, updated);
      toast?.current?.show({
        severity: "success",
        summary: "Préférences enregistrées",
        detail: `« ${preference.name} » ${enabled ? "activée" : "désactivée"}.`,
        life: 3000,
      });
    } catch (error) {
      toast?.current?.show({
        severity: "error",
        summary: "Erreur",
        detail: error.message,
        life: 5000,
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="card mb-4" data-testid={`admin-notifications-${membership.id}`}>
      {showSchoolName && (
        <div className="font-bold text-lg mb-3">
          <i className="pi pi-building mr-2" />
          {membership?.school?.name || "École"}
        </div>
      )}
      {isLoading && <ProgressSpinner style={{ width: "30px", height: "30px" }} />}
      {isError && (
        <small className="p-error">
          Impossible de charger vos préférences de notifications.
        </small>
      )}
      {!isLoading &&
        !isError &&
        preferences.map((preference) => {
          const inputId = `admin-notification-${membership.id}-${preference.id}`;
          return (
            <div
              key={preference.id}
              className="flex align-items-start gap-3 py-2 border-bottom-1 surface-border"
            >
              <InputSwitch
                inputId={inputId}
                checked={Boolean(preference.system_enabled && preference.enabled)}
                disabled={!preference.system_enabled || savingId === preference.id}
                onChange={(e) => handleToggle(preference, e.value)}
              />
              <div className="flex-1">
                <label htmlFor={inputId} className="font-medium block">
                  {preference.name}
                  {!preference.system_enabled && (
                    <Tag
                      className="ml-2"
                      severity="secondary"
                      value="Désactivée par la plateforme"
                    />
                  )}
                </label>
                <small className="text-500">{preference.description}</small>
              </div>
            </div>
          );
        })}
    </div>
  );
};

const AdminNotificationPreferences = ({ memberships, toast }) => {
  const adminMemberships = getAdminMemberships(memberships);
  if (adminMemberships.length === 0) {
    return null;
  }
  return (
    <div className="grid mb-5 mt-3 ml-5">
      <div className="col-12 lg:col-3">
        <div className="text-900 font-medium text-xl mb-3">
          Notifications de l'administration
        </div>
        <p className="m-0 p-0 text-400 line-height-2 text-sm mr-3">
          Choisissez les emails que vous souhaitez recevoir en tant
          qu'administrateur de l'école. Une notification désactivée par la
          plateforme ne peut pas être activée ici.
        </p>
      </div>
      <div className="col-12 lg:col-9">
        <div className="w-full lg:w-8">
          {adminMemberships.map((membership) => (
            <MembershipAdminNotifications
              key={membership.id}
              membership={membership}
              showSchoolName={adminMemberships.length > 1}
              toast={toast}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationPreferences;
